import { useEffect, useState } from "react";
import { ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

const TABS = [
  { key: "privacy_text", label: "Privacy", desc: "Informativa privacy completa mostrata ai clienti" },
  { key: "marketing_text", label: "Marketing", desc: "Testo di accettazione del consenso marketing + informativa breve" },
  { key: "cookie_text", label: "Protezione dati", desc: "Cookie policy e note sul trattamento" },
];

const SHORTCODES = [
  ["[[ragione_sociale]]", "Nome/ragione sociale del locale"],
  ["[[email]]", "Email di contatto"],
  ["[[telefono]]", "Telefono del locale"],
];

export default function PrivacySection({ readOnly }) {
  const [legal, setLegal] = useState(null);
  const [original, setOriginal] = useState(null);
  const [bozzaNote, setBozzaNote] = useState("");
  const [tab, setTab] = useState("privacy_text");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/settings/legal").then((r) => {
      setLegal(r.data.legal || {});
      setOriginal(r.data.legal || {});
      setBozzaNote(r.data.bozza_note || "");
    }).catch((e) => toast.error(apiError(e)));
  };
  useEffect(load, []);

  if (!legal) return <div className="text-sm text-zinc-400 py-8">Caricamento...</div>;

  const dirty = (legal[tab] || "") !== (original?.[tab] || "");

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/legal", { [tab]: legal[tab] || "" });
      toast.success("Testo legale salvato");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const annulla = () => setLegal({ ...legal, [tab]: original?.[tab] || "" });

  const ripristina = async () => {
    if (!window.confirm("Ripristinare la bozza standard? Il testo personalizzato verrà rimosso.")) return;
    setSaving(true);
    try {
      await api.put("/settings/legal", { [tab]: "" });
      toast.success("Ripristinata la bozza standard");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="privacy-section">
      <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-4 py-3" data-testid="legal-bozza-banner">
        {bozzaNote || "I testi predefiniti sono esemplificativi: modificali in base a come tratti i dati di cui sei titolare."}
      </div>

      <div className="flex gap-1.5 border-b border-zinc-200">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`legal-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
              tab === t.key ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <p className="text-xs text-zinc-500">{TABS.find((t) => t.key === tab)?.desc}</p>

      <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-3">
        <textarea value={legal[tab] || ""} readOnly={readOnly}
          onChange={(e) => setLegal({ ...legal, [tab]: e.target.value })}
          rows={16} data-testid="legal-editor"
          placeholder="Lascia vuoto per usare la bozza standard Dishenda..."
          className="w-full text-sm text-zinc-800 leading-relaxed border border-zinc-200 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-zinc-900 font-mono" />
        {tab === "marketing_text" && (
          <textarea value={legal.breve_text || ""} readOnly={readOnly}
            onChange={(e) => setLegal({ ...legal, breve_text: e.target.value })}
            rows={6} data-testid="legal-breve-editor"
            placeholder="Informativa breve mostrata nei moduli pubblici (vuoto = bozza standard)..."
            className="w-full text-sm text-zinc-800 leading-relaxed border border-zinc-200 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-zinc-900 font-mono" />
        )}
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400">Shortcode:</span>
          {SHORTCODES.map(([code, d]) => (
            <span key={code} title={d}
              className="text-[10px] font-mono bg-zinc-100 border border-zinc-200 rounded px-1.5 py-0.5 text-zinc-600">{code}</span>
          ))}
        </div>
        {!readOnly && (
          <div className="flex gap-2 pt-1">
            <button onClick={save} disabled={saving || !dirty} data-testid="legal-save"
              className="h-10 px-5 rounded-md bg-zinc-900 text-white text-sm font-bold hover:bg-zinc-700 active:scale-95 transition disabled:opacity-40">
              {saving ? "Salvataggio..." : "Salva"}
            </button>
            <button onClick={annulla} disabled={!dirty} data-testid="legal-annulla"
              className="h-10 px-5 rounded-md bg-white border border-zinc-300 text-sm font-semibold text-zinc-600 hover:bg-zinc-50 disabled:opacity-40">
              Annulla
            </button>
            <button onClick={ripristina} disabled={saving} data-testid="legal-ripristina"
              className="h-10 px-5 rounded-md bg-white border border-zinc-300 text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-40">
              Ripristina bozza standard
            </button>
          </div>
        )}
      </div>

      <div className="bg-sky-50 border border-sky-200 rounded-lg px-4 py-3 text-xs text-sky-800 flex gap-2">
        <ShieldCheck size={16} weight="bold" className="shrink-0 mt-0.5" />
        <p>
          I diritti GDPR dei singoli clienti (export dati e cancellazione/anonimizzazione) si gestiscono
          dalla scheda del cliente in <strong>Clienti → dettaglio → Privacy</strong>. Il link di
          disiscrizione viene aggiunto automaticamente in fondo a ogni email di marketing.
        </p>
      </div>
    </div>
  );
}
